import type { Identifier } from "ra-core";

import { getSupabaseClient } from "../providers/supabase/supabase";

export interface InstallerNotifyResult {
  sent: boolean;
  installer: string;
  to: string;
  photos: number;
}

/**
 * Emails the installation request to the order's installer (server side,
 * from notificaciones@guladeagua.es) and moves the order to "installer_notified".
 */
export const sendInstallerNotice = async (
  orderId: Identifier,
  installerId?: Identifier | null,
): Promise<InstallerNotifyResult> => {
  const { data, error } =
    await getSupabaseClient().functions.invoke<InstallerNotifyResult>(
      "installer_notify",
      {
        method: "POST",
        body: { order_id: orderId, installer_id: installerId ?? undefined },
      },
    );
  if (error || !data?.sent) {
    let message = "No se ha podido enviar el aviso";
    try {
      const body = await (error as { context?: Response })?.context?.json();
      if (body?.message) message = body.message;
    } catch {
      // keep the generic message
    }
    throw new Error(message);
  }
  return data;
};

interface InstallerParte {
  subject: string;
  html: string;
  text: string;
}

const fetchInstallerParte = async (
  orderId: Identifier,
  installerId?: Identifier | null,
): Promise<InstallerParte> => {
  const { data, error } =
    await getSupabaseClient().functions.invoke<InstallerParte>(
      "installer_notify",
      {
        method: "POST",
        body: {
          order_id: orderId,
          installer_id: installerId ?? undefined,
          render: true,
        },
      },
    );
  if (error || !data?.html)
    throw new Error("No se ha podido preparar el parte");
  return data;
};

/**
 * Opens the installer's parte (the same content as the email) in a new tab, ready
 * to print or save as PDF and send by WhatsApp. The tab opens before the request
 * so the browser does not block it as a pop-up.
 */
export const openInstallerParte = async (
  orderId: Identifier,
  installerId?: Identifier | null,
) => {
  const tab = window.open("", "_blank");
  if (!tab) throw new Error("El navegador ha bloqueado la ventana del parte");
  tab.document.title = "Parte de instalación";
  tab.document.body.textContent = "Preparando el parte…";
  try {
    const parte = await fetchInstallerParte(orderId, installerId);
    tab.document.open();
    tab.document
      .write(`<!doctype html><html lang="es"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${parte.subject.replace(/</g, "&lt;")}</title>
<style>body{margin:24px}@media print{.no-print{display:none}body{margin:0}}</style>
</head><body>
<p class="no-print" style="font-family:Arial,sans-serif"><button onclick="window.print()" style="padding:8px 14px;font-size:15px;cursor:pointer">Imprimir o guardar en PDF</button></p>
${parte.html}
</body></html>`);
    tab.document.close();
  } catch (error) {
    tab.close();
    throw error;
  }
};

/** The parte as plain text, to paste into WhatsApp. */
export const copyInstallerParte = async (
  orderId: Identifier,
  installerId?: Identifier | null,
) => {
  const parte = await fetchInstallerParte(orderId, installerId);
  await navigator.clipboard.writeText(parte.text);
};

/** Emails the customer the tracking number now (also to send it again). */
export const sendTrackingEmail = async (
  orderId: Identifier,
): Promise<{ to: string }> => {
  const { data, error } = await getSupabaseClient().functions.invoke<{
    sent: boolean;
    to: string;
  }>("tracking_notify", { method: "POST", body: { order_id: orderId } });
  if (error || !data?.sent) {
    let message = "No se ha podido enviar el seguimiento";
    try {
      const body = await (error as { context?: Response })?.context?.json();
      if (body?.message) message = body.message;
    } catch {
      // keep the generic message
    }
    throw new Error(message);
  }
  return data;
};
